<template>
  <div class="game-count-trend">
    <el-card class="chart-card">
      <div slot="header" class="card-header">
        <span>比赛级别趋势</span>
        <div class="operations">
          <el-select
            v-model="selectedLevels"
            multiple
            collapse-tags
            placeholder="选择比赛级别"
            class="type-select"
            @change="handleLevelChange"
          >
            <div class="select-actions">
              <el-button size="mini" type="text" @click.stop.prevent="handleSelectAll">全选</el-button>
              <el-divider direction="vertical" />
              <el-button size="mini" type="text" @click.stop.prevent="handleReset">重置</el-button>
            </div>
            <el-option
              v-for="item in allLevels"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExport" />
          </el-tooltip>
          <el-tooltip content="放大显示" placement="top">
            <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
          </el-tooltip>
        </div>
      </div>
      <div class="chart-container" ref="chart"></div>
    </el-card>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="90%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog vertical-center-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
      @close="handleDialogClose"
    >
      <div slot="title" class="dialog-header">
        <span>比赛数量趋势</span>
        <div class="dialog-operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          </el-tooltip>
          <el-tooltip content="关闭" placement="top">
            <el-button type="text" icon="el-icon-close" @click="closeDialog" />
          </el-tooltip>
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import _ from 'lodash'

// 添加辅助函数
function round(value, decimals) {
  return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals)
}

function sum(arr) {
  return arr.reduce((a, b) => a + b, 0)
}

export default {
  name: 'GameCountTrend',
  data() {
    return {
      chart: null,
      chartData: {
        months: [],
        levels: [],
        series: [],
        scores: {},
        ranks: {},
        totals: {}
      },
      fullscreenChart: null,
      dialogVisible: false,
      resizeHandler: null,
      isInitializing: false,
      selectedLevels: [],
      allLevels: []
    }
  },
  mounted() {
    this.initChart()
    this.fetchLevels()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
    if (this.fullscreenChart) {
      this.fullscreenChart.dispose()
    }
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.chart)
    },
    resizeChart() {
      this.chart?.resize()
    },
    async fetchData() {
      try {
        const res = await request.get('/api/analysis/game/count_trend')
        this.chartData = {
          months: res.data.months || [],
          levels: res.data.levels || [],
          series: res.data.series || [],
          scores: res.data.scores || {},
          ranks: res.data.ranks || {},
          totals: res.data.totals || {}
        }
        this.updateChart()
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败')
      }
    },
    calculateMonthlyAverages() {
      const monthlyData = new Map()
      
      this.chartData.months.forEach((month, index) => {
        let totalScore = 0
        let totalRank = 0
        let scoreCount = 0
        let rankCount = 0
        
        // 计算选中级别的总和
        this.selectedLevels.forEach(level => {
          const score = this.chartData.scores[level]?.[index]
          const rank = this.chartData.ranks[level]?.[index]
          const count = this.chartData.series.find(s => s.name === level)?.data[index] || 0
          
          // 只有当该级别在这个月有比赛时才计入平均值
          if (count > 0) {
            if (score !== null && score !== undefined) {
              totalScore += score * count  // 根据比赛数量加权
              scoreCount += count
            }
            if (rank !== null && rank !== undefined) {
              totalRank += rank * count    // 根据比赛数量加权
              rankCount += count
            }
          }
        })
        
        // 计算加权平均值
        monthlyData.set(month, {
          avgScore: scoreCount > 0 ? round(totalScore / scoreCount, 2) : null,
          avgRank: rankCount > 0 ? round(totalRank / rankCount, 2) : null
        })
      })

      return Array.from(monthlyData.entries()).map(([month, data]) => ({
        month,
        avgScore: data.avgScore,
        avgRank: data.avgRank
      })).sort((a, b) => a.month.localeCompare(b.month))
    },
    updateChart() {
      const monthlyAverages = this.calculateMonthlyAverages()
      
      // 重新构建柱状图数据
      const barData = this.chartData.months.map((_, index) => {
        const monthData = {}
        this.selectedLevels.forEach(level => {
          const series = this.chartData.series.find(s => s.name === level)
          if (series) {
            monthData[level] = series.data[index] || 0
          }
        })
        return monthData
      })

      // 颜色配置 - 为不同级别分配颜色
      const levelColors = [
        '#3498DB',  // 天蓝色
        '#E74C3C',  // 鲜红色
        '#2ECC71',  // 翠绿色
        '#9B59B6',  // 紫色
        '#F1C40F',  // 金黄色
        '#1ABC9C',  // 青绿色
        '#E67E22',  // 橙色
        '#34495E',  // 深蓝灰色
        '#C0392B',  // 深红色
        '#27AE60',  // 深绿色
        '#8E44AD',  // 深紫色
        '#F39C12',  // 琥珀色
        '#16A085',  // 深青色
        '#D35400',  // 深橙色
        '#7F8C8D',  // 灰色
        '#2980B9'   // 深天蓝色
      ]
      
      // 构建级别颜色映射
      const levelColorMap = {}
      this.selectedLevels.forEach((level, index) => {
        levelColorMap[level] = levelColors[index % levelColors.length]
      })
      
      // 构建新的系列数据
      const filteredSeries = this.selectedLevels.map((level, index) => ({
        name: level,
        type: 'bar',
        stack: 'total',
        barMaxWidth: 50,
        itemStyle: {
          color: levelColorMap[level],
          borderRadius: [4, 4, 0, 0]
        },
        emphasis: {
          focus: 'series'
        },
        data: barData.map(month => month[level] || 0)
      }))
      
      const option = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: function(params) {
            if (!params || !Array.isArray(params) || params.length === 0) return ''
            const title = params[0].axisValueLabel || ''
            
            // 颜色配置 - 与系列数据中的颜色保持一致
            const levelColors = [
              '#3498DB',  // 天蓝色
              '#E74C3C',  // 鲜红色
              '#2ECC71',  // 翠绿色
              '#9B59B6',  // 紫色
              '#F1C40F',  // 金黄色
              '#1ABC9C',  // 青绿色
              '#E67E22',  // 橙色
              '#34495E',  // 深蓝灰色
              '#C0392B',  // 深红色
              '#27AE60',  // 深绿色
              '#8E44AD',  // 深紫色
              '#F39C12',  // 琥珀色
              '#16A085',  // 深青色
              '#D35400',  // 深橙色
              '#7F8C8D',  // 灰色
              '#2980B9'   // 深天蓝色
            ]
            
            // 构建级别颜色映射
            const levelColorMap = {}
            this.selectedLevels.forEach((level, index) => {
              levelColorMap[level] = levelColors[index % levelColors.length]
            })
            
            // 获取级别标签样式
            const getLevelTagStyle = (level) => {
              const color = levelColorMap[level]
              if (!color) return null
              // 将颜色转换为 rgba 格式用于背景
              const hexToRgba = (hex, alpha) => {
                const r = parseInt(hex.slice(1, 3), 16)
                const g = parseInt(hex.slice(3, 5), 16)
                const b = parseInt(hex.slice(5, 7), 16)
                return `rgba(${r}, ${g}, ${b}, ${alpha})`
              }
              const bg = hexToRgba(color, 0.2)
              return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 11px; background-color: ${bg}; border: 1px solid ${color}; color: ${color}; line-height: 1.2;`
            }
            
            let content = ''
            params.forEach(param => {
              const seriesName = param.seriesName
              let value = param.value
              
              if (seriesName === '月度平均得分率') {
                value = value + '%'
                content += `<div>${seriesName}：${value}</div>`
              } else if (seriesName === '月度平均排位') {
                value = 'Top ' + value + '%'
                content += `<div>${seriesName}：${value}</div>`
              } else {
                // 检查是否是级别（在 selectedLevels 中）
                const isLevel = this.selectedLevels.includes(seriesName)
                if (isLevel && value > 0) {
                  const tagStyle = getLevelTagStyle(seriesName)
                  if (tagStyle) {
                    content += `<div><span style="${tagStyle}">${seriesName}</span>：${value}</div>`
                  } else {
                    content += `<div>${seriesName}：${value}</div>`
                  }
                } else if (value > 0) {
                  content += `<div>${seriesName}：${value}</div>`
                }
              }
            })
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${title}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  ${content}
                </div>
              </div>
            `
          }.bind(this)
        },
        legend: {
          data: [...this.selectedLevels, '月度平均得分率', '月度平均排位'],
          bottom: 0,
          left: 'center',
          itemGap: 20,
          textStyle: {
            fontSize: 12
          }
        },
        grid: {
          left: '0%',
          right: '0%',
          bottom: '15%',
          top: '30px',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.chartData.months,
          axisLabel: {
            interval: 0,
            rotate: 45,
            fontSize: 12,
            margin: 8
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '比赛数量',
            minInterval: 1,
            position: 'left',
            axisLabel: {
              formatter: '{value}',
              margin: 8
            },
            nameTextStyle: {
              padding: [0, 10, 0, 20]
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed'
              }
            }
          },
          {
            type: 'value',
            name: '得分率',
            min: 0,
            max: 100,
            interval: 20,
            position: 'right',
            offset: 20,
            axisLabel: {
              formatter: '{value}%',
              margin: 4,
              align: 'right'
            },
            nameTextStyle: {
              padding: [0, -8, 0, 20],
              align: 'right'
            },
            splitLine: {
              show: false
            }
          },
          {
            type: 'value',
            name: '排位',
            min: 0,
            max: 100,
            interval: 20,
            position: 'right',
            offset: 56,
            inverse: true,
            axisLabel: {
              formatter: '{value}%',
              margin: 4,
              align: 'right'
            },
            nameTextStyle: {
              padding: [0, 0, 0, 20],
              align: 'right'
            },
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          ...filteredSeries.map(series => ({
            ...series,
            barMaxWidth: 35,
            barGap: '10%',
          })),
          {
            name: '月度平均得分率',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: {
              width: 2,
              color: '#67C23A'
            },
            itemStyle: {
              color: '#67C23A'
            },
            emphasis: {
              focus: 'series'
            },
            z: 2,
            data: monthlyAverages.map(item => ({
              value: item.avgScore,
              name: item.month
            }))
          },
          {
            name: '月度平均排位',
            type: 'line',
            yAxisIndex: 2,
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: {
              width: 2,
              color: '#E6A23C'
            },
            itemStyle: {
              color: '#E6A23C'
            },
            emphasis: {
              focus: 'series'
            },
            z: 2,
            data: monthlyAverages.map(item => ({
              value: item.avgRank,
              name: item.month
            }))
          }
        ]
      }

      this.chart.setOption(option, true)
    },
    handleExport() {
      const dataURL = this.chart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })
      
      const link = document.createElement('a')
      link.download = '比赛数量趋势.png'
      link.href = dataURL
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    handleDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.fullscreenChart
          if (!chartDom) return

          this.fullscreenChart = echarts.init(chartDom)
          const option = this.chart.getOption()
          
          option.grid = {
            left: '5%',
            right: '5%',
            bottom: '10%',
            top: '10%',
            containLabel: true
          }

          this.fullscreenChart.setOption(option)
          
          this.resizeHandler = _.debounce(() => {
            if (this.fullscreenChart) {
              this.fullscreenChart.resize()
            }
          }, 100)

          window.addEventListener('resize', this.resizeHandler)
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleDialogClose() {
      this.cleanupFullscreen()
    },
    handleDialogClosed() {
      this.isInitializing = false
    },
    cleanupFullscreen() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }

      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    handleFullscreen() {
      this.cleanupFullscreen()
      this.dialogVisible = true
    },
    closeDialog() {
      this.dialogVisible = false
    },
    handleExportFullscreen() {
      if (!this.fullscreenChart) return
      
      try {
        const dataURL = this.fullscreenChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff'
        })
        
        const link = document.createElement('a')
        link.download = '比赛数量趋势.png'
        link.href = dataURL
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    async fetchLevels() {
      try {
        const res = await request.get('/api/dict/list', {
          params: {
            dict_key: 'game_level'
          }
        })
        this.allLevels = res.data.map(item => ({
          value: item.dict_value,
          label: item.dict_label
        }))
        // 默认全选
        this.selectedLevels = this.allLevels.map(level => level.value)
        // 获取完级别后再获取数据
        this.fetchData()
      } catch (error) {
        console.error('获取比赛级别失败:', error)
        this.$message.error('获取比赛级别失败')
      }
    },
    handleLevelChange() {
      this.fetchData()
    },
    handleSelectAll() {
      this.selectedLevels = this.allLevels.map(level => level.value)
      this.fetchData()
    },
    handleReset() {
      this.selectedLevels = []
      this.fetchData()
    }
  },
  watch: {
    dialogVisible(val) {
      if (!val && this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    selectedLevels: {
      handler(newVal) {
        if (this.chart) {
          this.updateChart()
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.game-count-trend {
  width: 100%;
  padding: 0;
  box-sizing: border-box;
}

.chart-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  height: 100%;
}

.chart-card :deep(.el-card__header) {
  padding: 0;
  border-bottom: none;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}

.chart-container {
  width: 100%;
  height: 400px;
  padding: 20px;
  box-sizing: border-box;
}

.operations {
  display: flex;
  gap: 8px;
  align-items: center;
}

.operations .el-button {
  color: #ffffff;
}

.operations .el-button:hover {
  color: #BD6C73;
}

.el-button [class^="el-icon-"] {
  font-size: 16px;
}

.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-operations {
  display: flex;
  gap: 8px;
}

.dialog-operations .el-button {
  color: #ffffff;
}

.dialog-operations .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 600px;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}

.year-select {
  width: 100px;
  margin-right: 8px;
}

.year-select :deep(.el-input__inner) {
  height: 28px;
  line-height: 28px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: #fff;
  padding-right: 25px;
}

.year-select :deep(.el-input__inner:hover) {
  background-color: rgba(255, 255, 255, 0.2);
}

.year-select :deep(.el-input__suffix) {
  right: 5px;
}

.year-select :deep(.el-select__caret) {
  color: #fff;
  line-height: 28px;
}

.year-select :deep(.el-select__caret.is-reverse) {
  transform: rotateZ(180deg);
}

.operations :deep(.el-select) {
  width: 160px;
  margin-right: 16px;
}

.operations :deep(.el-select .el-input__inner) {
  height: 28px;
  line-height: 28px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: #fff;
  padding-right: 25px;
}

.operations :deep(.el-select .el-input__inner:hover) {
  background-color: rgba(255, 255, 255, 0.2);
}

.operations :deep(.el-select .el-input__suffix) {
  right: 5px;
}

.operations :deep(.el-select .el-select__caret) {
  color: #fff;
  line-height: 28px;
}

.operations :deep(.el-select .el-select__tags) {
  background-color: transparent;
  height: 28px;
  line-height: 28px;
  padding: 0 4px;
}

.operations :deep(.el-select .el-tag) {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: transparent;
  color: #fff;
  height: 20px;
  line-height: 20px;
  margin: 4px 0;
  border-radius: 2px;
}

.operations :deep(.el-select .el-tag__close) {
  color: #fff;
  background-color: transparent;
}

.operations :deep(.el-select .el-tag__close:hover) {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.3);
}

.operations :deep(.el-select .el-select__input) {
  color: #fff;
  margin-left: 0;
}

.operations :deep(.el-select-dropdown__item) {
  height: 34px;
  line-height: 34px;
}

.operations :deep(.el-select-dropdown__item.selected) {
  color: #409EFF;
  font-weight: bold;
}

.operations :deep(.el-select-dropdown__item.hover) {
  background-color: rgba(64, 158, 255, 0.1);
}

.type-select {
  margin-right: 16px;
  width: 160px;
}

.type-select :deep(.el-input__inner) {
  height: 28px;
  line-height: 28px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: #fff;
  padding-right: 25px;
}

.type-select :deep(.el-input__inner:hover) {
  background-color: rgba(255, 255, 255, 0.2);
}

.type-select :deep(.el-input__suffix) {
  right: 5px;
}

.type-select :deep(.el-select__caret) {
  color: #fff;
  line-height: 28px;
}

.type-select :deep(.el-select__tags) {
  background-color: transparent;
  height: 28px;
  line-height: 28px;
  padding: 0 4px;
}

.type-select :deep(.el-tag) {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: transparent;
  color: #fff;
  height: 20px;
  line-height: 20px;
  margin: 4px 0;
  border-radius: 2px;
}

.type-select :deep(.el-tag__close) {
  color: #fff;
  background-color: transparent;
}

.type-select :deep(.el-tag__close:hover) {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.3);
}

.type-select :deep(.el-select__input) {
  color: #fff;
  margin-left: 0;
}

.type-select :deep(.el-select-dropdown__item) {
  height: 34px;
  line-height: 34px;
}

.type-select :deep(.el-select-dropdown__item.selected) {
  color: #409EFF;
  font-weight: bold;
}

.type-select :deep(.el-select-dropdown__item.hover) {
  background-color: rgba(64, 158, 255, 0.1);
}

.select-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-bottom: 1px solid #EBEEF5;
  width: 100%;
  height: 34px;
  line-height: 34px;
}

.select-actions .el-button {
  font-size: 14px;
  padding: 0;
  height: 34px;
  line-height: 34px;
  flex: 1;
  text-align: center;
}

.select-actions .el-divider--vertical {
  height: 14px;
  margin: 0 2px;
}

.type-select :deep(.el-select-dropdown__wrap) {
  margin-bottom: 0;
  max-height: 240px;
}

.type-select :deep(.el-select-dropdown__item) {
  height: 34px;
  line-height: 34px;
  font-size: 14px;
}

.type-select :deep(.el-divider--horizontal) {
  margin: 1px 0;
}
</style> 